import { useMemo } from "react"

import type { EmailAttachment } from "@productlane/api"
import type { FormattedAttachment } from "@productlane/ui/src/hooks/useAttachments"
import type {
	ChatMessage,
	Comment,
	Email,
	LiveChatMessage,
	SlackMessage,
} from "@productlane/zero"
import { convertHtmlToText } from "@productlane/lib/src/convert-html"
import { stripQuotedContent } from "@productlane/lib/src/html"
import { formatAttachments } from "@productlane/ui/src/helpers/format-email-attachments"
import { formatSlackAttachments } from "@productlane/ui/src/helpers/format-slack-attachments"

import { emailBody, emailBodyChunks } from "@/lib/email-body"

// Quoted replies, forwarded history and signatures never belong in a Linear issue.
function toPlainText(html: string): string {
	return convertHtmlToText(stripQuotedContent(html), true, {
		preserveLinks: true,
	})
}

// Zero types these columns as JSON strings, but a jsonb[] column arrives as
// objects, and JSON.parse on an object throws.
function parseAttachment(raw: unknown): FormattedAttachment | null {
	try {
		const parsed = (
			typeof raw === "string" ? JSON.parse(raw) : raw
		) as FormattedAttachment | null
		return parsed?.Content ? parsed : null
	} catch {
		return null
	}
}

function pushParsed(all: FormattedAttachment[], raws: readonly unknown[]) {
	for (const raw of raws) {
		const parsed = parseAttachment(raw)
		if (parsed) all.push(parsed)
	}
}

export function collectAllAttachments({
	threadAttachments,
	emails,
	comments,
	slackMessages,
	liveChatMessages,
}: {
	threadAttachments?: readonly string[] | null
	emails?: readonly Email[] | null
	comments?: readonly Comment[] | null
	slackMessages?: readonly SlackMessage[] | null
	liveChatMessages?: readonly LiveChatMessage[] | null
}): FormattedAttachment[] {
	const all: FormattedAttachment[] = []

	if (threadAttachments) pushParsed(all, threadAttachments)

	if (emails) {
		for (const email of emails) {
			const val = email.attachments
			if (val && Array.isArray(val)) {
				all.push(...formatAttachments(val as unknown[]))
			}
		}
	}

	if (comments) {
		for (const comment of comments) {
			if (comment.attachments) pushParsed(all, comment.attachments)
		}
	}

	if (slackMessages) {
		for (const msg of slackMessages) {
			const val = msg.attachments
			if (val && Array.isArray(val)) {
				all.push(...formatSlackAttachments(val as unknown[]))
			}
		}
	}

	if (liveChatMessages) {
		for (const msg of liveChatMessages) {
			if (msg.attachments) pushParsed(all, msg.attachments)
		}
	}

	return all
}

function formatAttachmentsAsMarkdown(
	attachments: FormattedAttachment[],
): string {
	if (attachments.length === 0) return ""

	const seen = new Set<string>()
	const unique = attachments.filter((a) => {
		if (!a.Content || !a.Content.startsWith("http") || seen.has(a.Content))
			return false
		seen.add(a.Content)
		return true
	})

	if (unique.length === 0) return ""

	const lines = unique.map((attachment) => {
		const name = attachment.Name || "Attachment"
		const url = attachment.Content
		const isImage = attachment.ContentType?.startsWith("image")
		return isImage ? `![${name}](${url})` : `[${name}](${url})`
	})

	return "\n\n---\n\n**Attachments:**\n\n" + lines.join("\n\n")
}

/**
 * Every message the customer side wrote, oldest first. One reply alone ("could
 * you log it as a feature request?") rarely says what the request is.
 */
export function customerMessagesText({
	emails,
	slackMessages,
	chatMessages,
	liveChatMessages,
}: {
	emails?: readonly Email[] | null
	slackMessages?: readonly SlackMessage[] | null
	chatMessages?: readonly ChatMessage[] | null
	liveChatMessages?: readonly LiveChatMessage[] | null
}): string {
	const messages: Array<{ createdAt: number; text: string }> = []

	for (const email of emails ?? []) {
		if (email.type !== "INBOUND") continue
		const attachments = (
			Array.isArray(email.attachments) ? email.attachments : []
		) as EmailAttachment[]
		const { html, text } = emailBody(email)
		const visible = emailBodyChunks(html, attachments).visible
		messages.push({
			createdAt: email.createdAt,
			text: visible ? toPlainText(visible) : text.trim(),
		})
	}
	for (const msg of slackMessages ?? []) {
		if (msg.type === "INBOUND")
			messages.push({ createdAt: msg.createdAt, text: toPlainText(msg.html) })
	}
	for (const msg of chatMessages ?? []) {
		if (msg.type === "INBOUND")
			messages.push({ createdAt: msg.createdAt, text: toPlainText(msg.html) })
	}
	for (const msg of liveChatMessages ?? []) {
		if (msg.role === "USER")
			messages.push({
				createdAt: msg.createdAt,
				text: toPlainText(msg.content),
			})
	}

	const texts: string[] = []
	for (const { text } of messages.sort((a, b) => a.createdAt - b.createdAt)) {
		const trimmed = text.trim()
		if (trimmed && trimmed !== texts.at(-1)) texts.push(trimmed)
	}
	return texts.join("\n\n")
}

/**
 * Builds a description string for creating a new Linear issue from a thread.
 * Extracts text from the thread body, emails, Slack messages, and attachments.
 */
export function useIssueDescription({
	highlighted,
	thread,
	slackMessages,
	emails,
	comments,
	liveChatMessages,
	chatMessages,
}: {
	highlighted: string
	thread: {
		text: string
		attachments?: readonly string[] | null
	}
	slackMessages?: readonly SlackMessage[] | null
	emails?: readonly Email[] | null
	comments?: readonly Comment[] | null
	liveChatMessages?: readonly LiveChatMessage[] | null
	chatMessages?: readonly ChatMessage[] | null
}) {
	const customerText = useMemo(
		() =>
			customerMessagesText({
				emails,
				slackMessages,
				chatMessages,
				liveChatMessages,
			}),
		[emails, slackMessages, chatMessages, liveChatMessages],
	)

	const allAttachments = useMemo(
		() =>
			collectAllAttachments({
				threadAttachments: thread.attachments,
				emails,
				comments,
				slackMessages,
				liveChatMessages,
			}),
		[thread.attachments, emails, comments, slackMessages, liveChatMessages],
	)

	const attachmentsMarkdown = useMemo(
		() => formatAttachmentsAsMarkdown(allAttachments),
		[allAttachments],
	)

	const deviceInfoText = useMemo(() => {
		if (!comments) return ""
		const systemComment = comments.find((c) => !c.userId)
		if (!systemComment) return ""
		return convertHtmlToText(systemComment.content, true).trim()
	}, [comments])

	const issueCreateDescription = useMemo(() => {
		const baseDescription =
			highlighted || customerText || toPlainText(thread.text)
		const parts = [baseDescription + attachmentsMarkdown]
		if (deviceInfoText) {
			parts.push("\n\n---\n\n" + deviceInfoText)
		}
		return parts.join("")
	}, [
		highlighted,
		customerText,
		thread.text,
		attachmentsMarkdown,
		deviceInfoText,
	])

	return issueCreateDescription
}
